import React, { useMemo } from 'react';
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
    ComposedChart
} from 'recharts';
import './PortfolioAnalyticsWithOptions.scss';
import { fontWeight } from '@mui/system';

const CustomTooltip = ({ active, payload, label, optionLines }) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const tolerance = 30;
    const nearbyOption = optionLines.find(opt => 
        Math.abs(opt.strike - label) < tolerance
    );

    if (nearbyOption) {
        const isCall = nearbyOption.optionType.toLowerCase() === 'call' || nearbyOption.optionType === 'C';
        return (
            <div className={`option-tooltip ${isCall ? 'call-option' : 'put-option'}`}>
                <div className={`tooltip-header ${isCall ? 'call-header' : 'put-header'}`}>
                    {nearbyOption.symbol} ({nearbyOption.optionType})
                </div>
                <div className="tooltip-content">
                    <div><strong>Strike (K):</strong> ${typeof nearbyOption.strikePrice === 'number' ? nearbyOption.strikePrice.toLocaleString() : nearbyOption.strikePrice}</div>
                    <div><strong>K_hat:</strong> {nearbyOption.strike.toFixed(6)}</div>
                    <div><strong>Quantity:</strong> {nearbyOption.quantity}</div>
                    <div><strong>Expiration:</strong> {nearbyOption.expirationDate}</div>
                    <div><strong>Beta (β):</strong> {typeof nearbyOption.beta === 'number' ? nearbyOption.beta.toFixed(6) : nearbyOption.beta}</div>
                    <div><strong>Historical Vol:</strong> {typeof nearbyOption.historicalVolatility === 'number' ? (nearbyOption.historicalVolatility * 100).toFixed(6) + '%' : nearbyOption.historicalVolatility}</div>
                    <div className="greeks-section">
                        <strong className="section-title">Greeks Contribution:</strong>
                    </div>
                    <div className="greeks-list">
                        <div><strong>Delta:</strong> {typeof nearbyOption.deltaContribution === 'number' ? nearbyOption.deltaContribution.toFixed(6) : nearbyOption.deltaContribution}</div>
                        <div><strong>Gamma:</strong> {typeof nearbyOption.gammaContribution === 'number' ? nearbyOption.gammaContribution.toFixed(6) : nearbyOption.gammaContribution}</div>
                        <div><strong>Theta:</strong> {typeof nearbyOption.thetaContribution === 'number' ? nearbyOption.thetaContribution.toFixed(6) : nearbyOption.thetaContribution}</div>
                        <div><strong>Rho:</strong> {typeof nearbyOption.rhoContribution === 'number' ? nearbyOption.rhoContribution.toFixed(6) : nearbyOption.rhoContribution}</div>
                    </div>
                    <div className="nominal-weight"><strong>Nominal Weight:</strong> {typeof nearbyOption.optionNominalWeight === 'number' ? (nearbyOption.optionNominalWeight * 100).toFixed(6) + '%' : nearbyOption.optionNominalWeight}</div>
                </div>
            </div>
        );
    }

    const payoffValue = payload[0]?.value;
    return (
        <div className="payoff-tooltip">
            <div className="tooltip-label">
                <strong>Index Level:</strong> {parseFloat(label).toLocaleString()}
            </div>
            <div className="tooltip-value">
                <strong>Expected Portfolio Payoff:</strong> ${payoffValue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
        </div>
    );
};

export default function PortfolioAnalyticsWithOptionsGraph({ 
    expectedTotalData, 
    levelGridData, 
    optionData, 
    indexTicker,
    hasOptionData
}) {
    const chartContainerRef = React.useRef(null);

    const chartData = useMemo(() => {
        if (!expectedTotalData || !levelGridData || expectedTotalData.length === 0 || levelGridData.length === 0) {
            return [];
        }
        return levelGridData.map((level, index) => ({
            indexLevel: level,
            payoff: expectedTotalData[index] || 0
        }));
    }, [expectedTotalData, levelGridData]);

    const optionLines = useMemo(() => {
        if (!hasOptionData || !optionData || optionData.length === 0 || !expectedTotalData || expectedTotalData.length === 0) {
            return [];
        }

        const K_count = {};
        const minPayoff = Math.min(...expectedTotalData);
        const maxPayoff = Math.max(...expectedTotalData);
        const payoffRange = maxPayoff - minPayoff;

        const processedOptions = [];

        optionData.forEach(option => {
            const k_hat = option.k_hat || option.ladder_strike_index_space;
            const symbol = option.ticker || option.Symbol;
            const optionType = option.option_type;
            
            if (k_hat && !isNaN(parseFloat(k_hat)) && symbol && symbol !== 'N/A' && optionType && optionType !== 'N/A') {
                const strikeValue = parseFloat(k_hat);
                
                K_count[strikeValue] = (K_count[strikeValue] || 0);                
                const y_shift = K_count[strikeValue] * 0.05 * payoffRange;
                const y_position = minPayoff + y_shift;
                K_count[strikeValue]++;
                
                const color = (optionType.toLowerCase() === 'put' || optionType === 'P') ? '#ff1744' : '#00c853';
                const strokeDasharray = (optionType.toLowerCase() === 'put' || optionType === 'P') ? "5 5" : "5 5";
                
                processedOptions.push({
                    strike: strikeValue,
                    symbol,
                    optionType,
                    color,
                    strokeDasharray,
                    y_position: y_position,
                    label: `${symbol}\n(${optionType.toLowerCase() === 'put' || optionType === 'P' ? 'P' : 'C'})`,
                    quantity: option.quantity || 'N/A',
                    strikePrice: option.strike_price || 'N/A',
                    expirationDate: option.expiration_date || 'N/A',
                    beta: option.beta || 'N/A',
                    historicalVolatility: option.historical_volatility || 'N/A',
                    deltaContribution: option.delta_contribution || 'N/A',
                    gammaContribution: option.gamma_contribution || 'N/A',
                    thetaContribution: option.theta_contribution || 'N/A',
                    rhoContribution: option.rho_contribution || 'N/A',
                    optionNominalWeight: option.option_nominal_weight || 'N/A'
                });
            }
        });

        return processedOptions;
    }, [optionData, expectedTotalData, hasOptionData]);

    const yAxisDomain = useMemo(() => {
        if (!expectedTotalData || expectedTotalData.length === 0) {
            return ['auto', 'auto'];
        }
        
        const minValue = Math.min(...expectedTotalData);
        const maxValue = Math.max(...expectedTotalData);
        
        return [minValue, maxValue];
    }, [expectedTotalData]);

    const xAxisDomain = useMemo(() => {
        if (!chartData || chartData.length === 0) return ['auto', 'auto'];
        
        const dataMinX = Math.min(...chartData.map(d => d.indexLevel));
        const dataMaxX = Math.max(...chartData.map(d => d.indexLevel));
        
        let domainMin = dataMinX;
        let domainMax = dataMaxX;
        
        if (optionLines && optionLines.length > 0) {
            const strikeValues = optionLines.map(opt => opt.strike).filter(s => !isNaN(s) && isFinite(s));
            if (strikeValues.length > 0) {
                const range = dataMaxX - dataMinX;
                const maxExtension = range * 0.5;
                
                const validStrikes = strikeValues.filter(strike => 
                    strike >= (dataMinX - maxExtension) && strike <= (dataMaxX + maxExtension)
                );
                
                if (validStrikes.length > 0) {
                    domainMin = Math.min(domainMin, ...validStrikes);
                    domainMax = Math.max(domainMax, ...validStrikes);
                }
            }
        }
        
        return [domainMin, domainMax];
    }, [chartData, optionLines]);

    const formatYAxis = (tickItem) => {
        return `$${tickItem.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
    };

    const formatXAxis = (tickItem) => {
        return tickItem.toLocaleString();
    };

    if (chartData.length === 0) {
        return (
            <div className="portfolio-options-chart">
                <div className="chart-loading">
                    <p>Loading chart data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="portfolio-options-chart">
            <div className="chart-header">
                <h3 className="chart-title">
                    Intrinsic Value (Equity Value + Moneyness of Options)
                    <br />
                    <span className="chart-title-vs">vs</span>
                    <br />
                    {indexTicker}
                </h3>
            </div>
            <div className="chart-wrapper" ref={chartContainerRef}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{
                            top: 40,
                            right: 30,
                            left: 80,
                            bottom: 100
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#d0d0d0" opacity={0.7} />
                        <XAxis 
                            dataKey="indexLevel" 
                            type="number"
                            scale="linear"
                            domain={xAxisDomain}
                            label={{ 
                                value: `Index Level (${indexTicker})`, 
                                position: 'insideBottom', 
                                offset: -20,
                                style: { fontSize: '14px', fill: '#6b7280', fontWeight: 'bold'}
                            }}
                            stroke="#9ca3af"
                            tickFormatter={formatXAxis}
                            tick={{ fontSize: '13px', fill: '#6b7280' }}
                        />
                        <YAxis 
                            label={{ 
                                value: 'Intrinsic Value', 
                                angle: -90, 
                                offset: -20,
                                position: 'insideLeft',
                                style: { fontSize: '14px', fill: '#6b7280', fontWeight: 'bold'}
                            }}
                            domain={yAxisDomain}
                            tickFormatter={formatYAxis}
                            stroke="#9ca3af"
                            tick={{ fontSize: '13px', fill: '#6b7280' }}
                        />
                        <Tooltip 
                            content={<CustomTooltip optionLines={optionLines} />}
                            cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }}
                        />
                        <Legend 
                            verticalAlign="bottom" 
                            align="right"
                            wrapperStyle={{ paddingTop: '6px' }}
                            payload={[
                                { value: 'Expected Portfolio Payoff', type: 'line', color: '#3b82f6' },
                                { value: 'Call Options', type: 'line', color: '#00c853', payload: { strokeDasharray: '5 5' } },
                                { value: 'Put Options', type: 'line', color: '#ff1744', payload: { strokeDasharray: '5 5' } }
                            ]}
                        />
                        
                        <Line 
                            type="monotone" 
                            dataKey="payoff" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                                r: 4,
                                fill: '#3b82f6',
                                strokeWidth: 2,
                                stroke: 'white'
                            }}
                            name="Expected Portfolio Payoff"
                            connectNulls={true}
                        />

                        {optionLines.map((optionLine, index) => (
                            <ReferenceLine
                                key={`ref-line-${index}`}
                                x={optionLine.strike}
                                stroke={optionLine.color}
                                strokeWidth={2}
                                strokeDasharray={optionLine.strokeDasharray}
                                opacity={1}
                                isFront={true}
                                label={{
                                    value: optionLine.symbol + (optionLine.optionType.toLowerCase() === 'put' || optionLine.optionType === 'P' ? ' (P)' : ' (C)'),
                                    position: 'center',
                                    fill: optionLine.color,
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    angle: -90,
                                    style: {
                                        textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white',
                                        fontFamily: 'Arial, sans-serif'
                                    }
                                }}
                            />
                        ))}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}


